import type { StorefrontFamily, StorefrontFilterValue } from "../types";

/** Shop browsing on the home page: category pills, then type and theme filters and a sort, like most online shops. */
export type SortKey = "recommended" | "price-asc" | "price-desc" | "newest" | "name";
export type Browse = { category: string; type: string; theme: string; sort: SortKey };

export const sortLabels: Record<SortKey, string> = { recommended: "Recommended", "price-asc": "Price: low to high", "price-desc": "Price: high to low", newest: "Newest", name: "Name: A to Z" };

/** The lowest price a shopper can pay for the family (in-stock options first). */
export function familyPrice(family: StorefrontFamily): number {
  const prices = (family.variants.some((variant) => variant.availability === "in_stock") ? family.variants.filter((variant) => variant.availability === "in_stock") : family.variants).map((variant) => variant.price);
  return prices.length ? Math.min(...prices) : Number.POSITIVE_INFINITY;
}

const familyLabel = (family: StorefrontFamily) => (family.cxFacingName?.trim() || family.title?.trim() || family.variants[0]?.title || "").toLocaleLowerCase();

/** Distinct values across families, in the order the catalogue first lists them. */
export function filterValues(families: StorefrontFamily[], key: "types" | "themes"): StorefrontFilterValue[] {
  return [...new Map(families.flatMap((family) => family[key] ?? []).map((value) => [value.code, value])).values()];
}

export const inCategory = (families: StorefrontFamily[], category: string) => (category ? families.filter((family) => family.category?.code === category) : families);
const hasValue = (family: StorefrontFamily, key: "types" | "themes", code: string) => !code || (family[key] ?? []).some((value) => value.code === code);

/** Category, then type, then theme; the sort is stable so "Recommended" keeps the catalogue order. */
export function browseFamilies(families: StorefrontFamily[], browse: Browse): StorefrontFamily[] {
  const shown = inCategory(families, browse.category).filter((family) => hasValue(family, "types", browse.type) && hasValue(family, "themes", browse.theme));
  const compare: Record<SortKey, ((left: StorefrontFamily, right: StorefrontFamily) => number) | null> = {
    recommended: null,
    "price-asc": (left, right) => familyPrice(left) - familyPrice(right),
    "price-desc": (left, right) => familyPrice(right) - familyPrice(left),
    newest: (left, right) => right.familyId - left.familyId,
    name: (left, right) => familyLabel(left).localeCompare(familyLabel(right)),
  };
  const by = compare[browse.sort];
  return by ? [...shown].sort(by) : shown;
}
